import React from 'react';
import { ArrowRightIcon, SendIcon, TrendingUpIcon, HomeIcon } from 'lucide-react';
import { MetricCard } from './MetricCard';
import { BrandBanner } from './BrandBanner';
interface HeroProps {
  'data-id'?: string;
  videoUrl?: string;
}
export function Hero({
  'data-id': dataId,
  videoUrl = 'REPLACE_WITH_YOUR_VIDEO_URL'
}: HeroProps) {
  return <div className="relative flex flex-col items-center justify-start w-full max-w-[1280px] mx-auto pt-12 md:pt-16 lg:pt-20 pb-12 md:pb-16 bg-gradient-to-b from-[#121212] via-[#0f0f0f] to-[#121212] text-white font-sans overflow-visible px-6 md:px-8" data-id="element-1496">
      {/* Floating Particles Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1497">
        {[...Array(20)].map((_, i) => <div key={i} className="absolute w-1 h-1 bg-[#3ab795] rounded-full opacity-20" style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animation: `float-particle ${15 + Math.random() * 15}s ease-in-out infinite`,
        animationDelay: `${Math.random() * 5}s`
      }} data-id="element-1498" />)}
      </div>

      {/* Hero Content */}
      <div className="relative flex flex-col items-center justify-center w-full max-w-[1050px] overflow-visible mb-12 md:mb-16 lg:mb-20 z-10" data-id="element-1499">
        <div className="flex flex-col items-center justify-center w-full relative overflow-visible" data-id="element-1500">
          {/* Launching Soon Badge */}
          <div className="mb-6 md:mb-8" data-id="element-1501">
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-[#3ab795] rounded-full shadow-lg animate-pulse-subtle" data-id="element-1502">
              <span className="text-sm md:text-base font-bold text-white uppercase tracking-wide" data-id="element-1503">
                Spots Filling Fast
              </span>
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" data-id="element-1504" />
            </div>
          </div>

          {/* Headline */}
          <div className="relative flex flex-col items-center justify-center w-full transform-none mb-6 md:mb-8" data-id="element-1505">
            <h1 className="relative text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight tracking-tight text-center px-4" data-id="element-1506">
              <span className="text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.3)]" data-id="element-1507">
                The Fastest Way To Get{' '}
              </span>
              <span className="relative inline-block" data-id="element-1508">
                <span className="text-[#3ab795] drop-shadow-[0_0_40px_rgba(58,183,149,0.8)]" data-id="element-1509">
                  1–3 Extra Deals
                </span>
                <span className="absolute -bottom-1 md:-bottom-2 left-0 right-0 h-0.5 md:h-1 bg-[#3ab795]/30 rounded-full" data-id="element-1510"></span>
                <span className="absolute inset-0 blur-2xl bg-[#3ab795] opacity-20 -z-10" data-id="element-1511"></span>
              </span>
              <span className="text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.3)]" data-id="element-1512">
                {' '}
                Per Month From The MLS
              </span>
            </h1>
          </div>

          {/* Subheadline */}
          <div className="relative flex flex-col items-center justify-center w-full max-w-[660px] transform-none px-4" data-id="element-1513">
            <p className="text-[#b8bcc8] text-center text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed font-light m-0 p-0" data-id="element-1514">
              Most investors send 5–10 offers per week.
              <br data-id="element-1515" />
              Our clients send{' '}
              <span className="font-semibold text-white" data-id="element-1516">1,000+</span> and let
              math create contracts.
            </p>
          </div>
        </div>
      </div>

      {/* Video Hero Section with Floating Cards */}
      <div className="z-7 flex flex-row items-center justify-center w-full max-w-[1034px] min-h-[200px] sm:min-h-[300px] md:min-h-[400px] lg:min-h-[486px] relative overflow-visible p-0 gap-0 mx-auto mt-0 mb-8 md:mb-12" data-id="element-1517">
        {/* Floating Metric Cards - Hidden on mobile, visible on lg+ */}
        <div className="hidden lg:block z-20 absolute top-[-110px] left-[-120px] scale-[0.45] transition-all duration-500 hover:scale-[0.48] hover:z-30 cursor-pointer animate-float-card-left origin-center" role="presentation" data-id="element-1518">
          <MetricCard title="Offers Sent Today" value="312" subtitle="sent this morning" progress={85} progressLabel="Daily Target" trend="+47" trendUp={true} variant="default" icon={<SendIcon size={16} strokeWidth={2} data-id="element-1520" />} rotate={-2} data-id="element-1519" />
        </div>

        <div className="hidden lg:block z-20 absolute top-[-125px] left-[50%] -translate-x-1/2 scale-[0.45] transition-all duration-500 hover:scale-[0.48] hover:z-30 cursor-pointer animate-float-card-center origin-center" role="presentation" data-id="element-1521">
          <MetricCard title="Agent Opens" value="18.7%" subtitle="opens this week" progress={78} progressLabel="Weekly Goal" trend="+2.4%" trendUp={true} variant="default" icon={<TrendingUpIcon size={16} strokeWidth={2} data-id="element-1523" />} rotate={0} data-id="element-1522" />
        </div>

        <div className="hidden lg:block z-20 absolute top-[-117px] right-[-120px] scale-[0.45] transition-all duration-500 hover:scale-[0.48] hover:z-30 cursor-pointer animate-float-card-right origin-center" role="presentation" data-id="element-1524">
          <MetricCard title="Properties Under Contract" value="8" subtitle="active contracts" progress={67} progressLabel="Monthly Target" trend="+2" trendUp={true} variant="primary" icon={<HomeIcon size={16} strokeWidth={2} data-id="element-1526" />} rotate={1.5} data-id="element-1525" />
        </div>

        {/* Video Section */}
        <div className="w-full max-w-[1034px] relative rounded-xl md:rounded-2xl shadow-[0_0_40px_15px_rgba(255,255,255,0.25),0_0_20px_5px_rgba(255,255,255,0.15),0px_4px_16px_0px_rgba(0,0,0,0.2)] md:shadow-[0_0_80px_30px_rgba(255,255,255,0.35),0_0_40px_10px_rgba(255,255,255,0.2),0px_8px_24px_0px_rgba(0,0,0,0.3)] transition-all duration-500 hover:shadow-[0_0_80px_30px_rgba(255,255,255,0.35),0_0_40px_10px_rgba(255,255,255,0.2),0px_12px_32px_0px_rgba(0,0,0,0.4)] hover:scale-[1.02] z-10 overflow-hidden" data-id="element-1527">
          <div style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          padding: 0
        }} data-id="element-1528">
            <iframe src="https://www.loom.com/embed/e51be912310149d5883cc59283c0a11e?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true" className="absolute inset-0 w-full h-full rounded-[inherit]" frameBorder="0" allowFullScreen allow="autoplay; fullscreen" title="AcquireFlow Demo" data-id="element-1529" />
          </div>
        </div>
      </div>

      {/* CTA - Below Video */}
      <div className="flex items-center justify-center w-full mt-8 md:mt-10 px-4" data-id="element-1530">
        <a href="https://app.acquireflow.ai/signup" target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center gap-3 bg-[#3ab795] text-white px-8 py-4 rounded-xl font-semibold text-lg hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:ring-offset-2 focus:ring-offset-[#121212] shadow-[0_0_30px_rgba(58,183,149,0.4),0_4px_16px_rgba(58,183,149,0.3)] hover:shadow-[0_0_50px_rgba(58,183,149,0.6),0_8px_24px_rgba(58,183,149,0.5)]" aria-label="Start your free trial" data-id="element-1531">
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#3ab795] to-[#2d9575] opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-2xl -z-10" data-id="element-1532" />
          <span data-id="element-1533">Start Free Trial</span>
          <ArrowRightIcon size={20} className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2.5} data-id="element-1534" />
        </a>
      </div>

      {/* Brand Banner */}
      <div className="w-full mt-8 md:mt-12" data-id="element-1535">
        <BrandBanner data-id="element-1536" />
      </div>
    </div>;
}